import { clamp } from '../core/math';
import { normalizeText } from '../core/text';
import type { DayNumber } from '../core/time';
import {
  B,
  PREF_NAMES,
  T_MAX,
  T_MIN,
  actualAtBase,
  resetGrade,
  setGrade,
  simulate,
  stateAt,
} from '../model';
import type { Plan, Simulation, Site, SiteState } from '../model';
import {
  GRADE_FIELDS,
  PLAN_FIELDS,
  isGradeKey,
  isPlanNumberKey,
  isSoilKey,
  normalizeInput,
} from './planInput';
import type { GradeKey, PlanNumberKey } from './planInput';

/** 種類の絞り込み。`delay` は竣工が計画より 15 日以上遅れる予測の現場。 */
export type Chip = 'all' | '建築' | '土木' | 'delay';
export type Speed = 7 | 30 | 90;
export const CHIP_KEYS: readonly Chip[] = ['all', '建築', '土木', 'delay'];
export const SPEEDS: readonly Speed[] = [7, 30, 90];
const isChip = (v: unknown): v is Chip => (CHIP_KEYS as readonly unknown[]).includes(v);
const isSpeed = (v: unknown): v is Speed => (SPEEDS as readonly unknown[]).includes(v);
/** 遅延予測の区分。late＝15 日以上、warn＝1〜14 日、ok＝計画内。 */
export type DelayLevel = 'late' | 'warn' | 'ok';
/**
 * 何が変わったか。画面は、変わった部分だけを描き直す。
 * `plan` は施工計画の値の変更、`planReset` は初期計画に戻したこと（入力欄も描き直す）。
 */
export type Change = 'time' | 'filter' | 'selection' | 'plan' | 'planReset' | 'play' | 'mark';

/** 住所照合で選んだ地点の印（元仕様 §9。白い点と「📍 住所」の札）。 */
export interface Mark {
  label: string;
  lon: number;
  lat: number;
}

export interface AppState {
  /** 選んでいる現場の ID。 */
  sel: string | null;
  /** 検索欄の文字。 */
  q: string;
  chip: Chip;
  /** 選んでいる都道府県。空は全国。 */
  pref: string;
  /** 表示日（ワールド時刻）。 */
  T: DayNumber;
  playing: boolean;
  /** 再生の速さ（日/秒）。 */
  speed: Speed;
  /** 住所照合の印。現場を選ぶと消える。 */
  mark: Mark | null;
}

/** 現場と、いまの施工計画での予測。 */
export interface SiteView {
  site: Site;
  /** いまの施工計画（What-if で変わる。保存しない）。 */
  plan: Plan;
  /** いまの施工計画での予測。 */
  cur: Simulation;
  /** 初期の施工計画での予測（比較の基準）。 */
  base: Simulation;
  /** 表示日の状態。 */
  state: SiteState;
}

export interface Summary {
  count: number;
  /** 平均進捗（基準日の実績、%）。 */
  avgActualPct: number;
  /** 遅延予測（14 日超）の現場数。 */
  late: number;
  /** 切土量の合計（地山 m³）と残土の合計（ほぐし m³）。 */
  cut: number;
  spoil: number;
}

export const delayLevel = (delay: number): DelayLevel =>
  delay > 14 ? 'late' : delay > 0 ? 'warn' : 'ok';

type Listener = (changes: ReadonlySet<Change>) => void;

/** 画面の状態を 1 か所で持つ。DOM を知らない。 */
export class Store {
  readonly views: SiteView[];
  private current: AppState = {
    sel: null,
    q: '',
    chip: 'all',
    pref: '',
    T: B,
    playing: false,
    speed: 30,
    mark: null,
  };
  private readonly listeners = new Set<Listener>();

  constructor(sites: readonly Site[]) {
    this.views = sites.map((site) => {
      const plan = { ...site.p0 };
      const cur = simulate(site, plan);
      return { site, plan, cur, base: cur, state: stateAt(site, B, cur) };
    });
  }

  get state(): Readonly<AppState> {
    return this.current;
  }

  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private emit(...changes: Change[]): void {
    const set = new Set(changes);
    for (const fn of this.listeners) fn(set);
  }

  view(id: string | null): SiteView | undefined {
    return id === null ? undefined : this.views.find((v) => v.site.id === id);
  }

  get selected(): SiteView | undefined {
    return this.view(this.current.sel);
  }

  // ---- 表示日 ----
  setT(T: DayNumber): void {
    // 数でない日付は捨てる（NaN は clamp を素通りし、全現場の状態を壊すため）
    if (!Number.isFinite(T)) return;
    const next = clamp(T, T_MIN, T_MAX);
    if (next === this.current.T) return;
    this.current = { ...this.current, T: next };
    for (const v of this.views) v.state = stateAt(v.site, next, v.cur);
    this.emit('time');
  }

  /** 再生を止めて基準日に戻る。 */
  toBase(): void {
    const wasPlaying = this.current.playing;
    this.current = { ...this.current, playing: false };
    this.setT(B);
    if (wasPlaying) this.emit('play');
  }

  /** 再生と停止を切りかえる。最後まで行っていたら基準日から再生する。 */
  togglePlay(): void {
    if (!this.current.playing && this.current.T >= T_MAX - 1) this.setT(B);
    this.current = { ...this.current, playing: !this.current.playing };
    this.emit('play');
  }

  /** 再生の速さを変える。7・30・90 日/秒以外は捨てる。 */
  setSpeed(speed: Speed): void {
    if (!isSpeed(speed)) return;
    this.current = { ...this.current, speed };
    this.emit('play');
  }

  /** 再生中なら、経過秒数の分だけ表示日を進める。最後まで行くと止まる。 */
  tick(seconds: number): void {
    if (!this.current.playing) return;
    const next = this.current.T + this.current.speed * seconds;
    if (next >= T_MAX) {
      this.current = { ...this.current, playing: false };
      this.setT(T_MAX);
      this.emit('play');
    } else {
      this.setT(next);
    }
  }

  // ---- 絞り込みと選択 ----
  setQuery(q: string): void {
    this.current = { ...this.current, q };
    this.emit('filter');
  }

  /** 種類で絞り込む。定義にない値は捨てる。 */
  setChip(chip: Chip): void {
    if (!isChip(chip)) return;
    this.current = { ...this.current, chip };
    this.emit('filter');
  }

  /** 都道府県で絞り込む。選んでいる現場がその県になければ、選択を外す。 */
  setPref(pref: string): void {
    // 空（全国）か、地域係数を持つ 47 都道府県の名前だけを受け付ける
    if (pref !== '' && !PREF_NAMES.has(pref)) return;
    const sel = this.selected && pref && this.selected.site.pref !== pref ? null : this.current.sel;
    const selectionChanged = sel !== this.current.sel;
    this.current = { ...this.current, pref, sel };
    if (selectionChanged) this.emit('filter', 'selection');
    else this.emit('filter');
  }

  select(id: string | null): void {
    const sel = id !== null && this.view(id) ? id : null;
    if (sel === this.current.sel) return;
    // 現場を選ぶと住所の印は消す（サンプルと同じ）
    const mark = sel === null ? this.current.mark : null;
    this.current = { ...this.current, sel, mark };
    this.emit('selection');
  }

  /**
   * 住所照合の印を置く。現場の選択は外す（全国表示で印を見せるため）。
   * 経度・緯度が数でなければ置かない。null で消す。
   */
  setMark(mark: Mark | null): void {
    if (mark && !(Number.isFinite(mark.lon) && Number.isFinite(mark.lat) && mark.label)) return;
    const changedSel = mark !== null && this.current.sel !== null;
    this.current = { ...this.current, mark, sel: mark ? null : this.current.sel };
    if (changedSel) this.emit('mark', 'selection');
    else this.emit('mark');
  }

  matches(v: SiteView): boolean {
    const { pref, chip, q } = this.current;
    const s = v.site;
    if (pref && s.pref !== pref) return false;
    if (chip === '建築' && s.type !== '建築') return false;
    if (chip === '土木' && s.type !== '土木') return false;
    if (chip === 'delay' && v.cur.delay <= 14) return false;
    const nq = normalizeText(q);
    if (!nq) return true;
    return [s.id, s.name, s.addr, s.client, s.type, s.kind.label, s.dept].some((x) =>
      normalizeText(x).includes(nq),
    );
  }

  /** 絞り込んだ現場の一覧（ID の昇順）。 */
  filtered(): SiteView[] {
    return this.views.filter((v) => this.matches(v));
  }

  /** 選んだ都道府県（なければ全国）の現場。検索欄と種類の絞り込みは効かない。 */
  inPref(): SiteView[] {
    return this.views.filter((v) => !this.current.pref || v.site.pref === this.current.pref);
  }

  summary(): Summary {
    const set = this.inPref();
    return {
      count: set.length,
      avgActualPct: set.length
        ? Math.round((set.reduce((a, v) => a + actualAtBase(v.site), 0) / set.length) * 100)
        : 0,
      late: set.filter((v) => v.cur.delay > 14).length,
      cut: set.reduce((a, v) => a + v.site.g.Vc, 0),
      spoil: set.reduce((a, v) => a + v.cur.eb.outLoose, 0),
    };
  }

  /** 現場のある都道府県と、その現場数。 */
  prefCounts(): Map<string, number> {
    const m = new Map<string, number>();
    for (const v of this.views) m.set(v.site.pref, (m.get(v.site.pref) ?? 0) + 1);
    return m;
  }

  // ---- 施工計画の変更（What-if。FR-EARTH-05、FR-PLAN-07、FR-SIM-04・05） ----
  /** 現場の予測を、いまの施工計画で計算し直す。 */
  private resim(v: SiteView): void {
    v.cur = simulate(v.site, v.plan);
    v.state = stateAt(v.site, this.current.T, v.cur);
  }

  /**
   * 施工計画の数値を 1 つ変える。数でない値は捨て、範囲の外は範囲に収める。
   * 値が変わったら予測を計算し直して true を返す。保存はしない。
   */
  setPlanValue(id: string, key: PlanNumberKey, raw: string | number): boolean {
    const v = this.view(id);
    if (!v || !isPlanNumberKey(key)) return false;
    const value = normalizeInput(PLAN_FIELDS[key], raw);
    if (!v || value === null || v.plan[key] === value) return false;
    v.plan = { ...v.plan, [key]: value };
    this.resim(v);
    this.emit('plan');
    return true;
  }

  /** 土質を変える。定義済みの土質でなければ捨てる。 */
  setSoil(id: string, soil: string): boolean {
    const v = this.view(id);
    if (!v || !isSoilKey(soil) || v.plan.soil === soil) return false;
    v.plan = { ...v.plan, soil };
    this.resim(v);
    this.emit('plan');
    return true;
  }

  /**
   * 計画地盤高または根切り深さを変える。計画面と土量を作り直し、
   * 比較の基準（初期の施工計画での予測）も新しい地面で計算し直す。η は変えない。
   */
  setGradeValue(id: string, key: GradeKey, raw: string | number): boolean {
    const v = this.view(id);
    // 根切りのない工種（宅地造成）は根切り深さを変えない
    if (!v || !isGradeKey(key) || (key === 'pit' && v.site.kind.pit <= 0)) return false;
    const value = normalizeInput(GRADE_FIELDS[key], raw);
    if (!v || value === null || v.site[key] === value) return false;
    setGrade(v.site, { [key]: value });
    v.base = simulate(v.site, v.site.p0);
    this.resim(v);
    this.emit('plan');
    return true;
  }

  /** 施工計画と計画地盤高・根切り深さを、すべて初期値に戻す。 */
  resetPlan(id: string): void {
    const v = this.view(id);
    if (!v) return;
    v.plan = { ...v.site.p0 };
    resetGrade(v.site);
    v.base = simulate(v.site, v.site.p0);
    this.resim(v);
    this.emit('plan', 'planReset');
  }

  /** 初期計画から変えているかどうか。 */
  isWhatIf(v: SiteView): boolean {
    const { site, plan } = v;
    return (
      site.FH !== site.FH0 ||
      site.pit !== site.kind.pit ||
      (Object.keys(plan) as (keyof Plan)[]).some((k) => plan[k] !== site.p0[k])
    );
  }
}
